'use strict';

// core version + navigation, pagination modules:
import Swiper from '@jsthirdparty/swiper';
// import Swiper and modules styles
import Pagination from '@jsthirdparty/swiper/modules/pagination.mjs';
import Zoom from '@jsthirdparty/swiper/modules/zoom.mjs';
import {
  classes,
  addClassToElement,
  removeClassToElement,
  hasClassElement
} from '@jspartials/class-list-utils';
import { mediaQuery, getMediaQuery } from '@jspartials/general-utils';
import {
  setHtmlScrollable,
  setHtmlUnscrollable
} from '../../partials/general-utils';

// Classes
const className = {
  transformNone: '!tw-transform-none'
};

// Selectors
const selector = {
  carousel: {
    wrapper: '.js-carousel',
    inner: '.js-carousel__inner',
    button: {
      zoom: '.js-carousel__zoom-button',
      closeFullscreen: '.js-carousel__close-fullscreen'
    }
  },
  bottomBarInfo: '.js-bottom-bar-info',
  swiper: {
    wrapper: '.swiper',
    pagination: '.swiper-pagination'
  },
  imageFullscreen: '.js-image-fullscreen'
};

// Elements
const element = {
  carousel: {
    wrapper: null,
    inner: null,
    button: {
      zoom: null,
      closeFullscreen: null
    }
  },
  bottomBarInfo: null
};

// Variables
let swiper = null;
let htmlElementScroll = 0;
const mediaQueryTablet = getMediaQuery(mediaQuery.tablet);

// Functions

/**
 * Initializes the elements
 * @returns {void}
 */
const initElements = () => {
  element.carousel.wrapper =
    document.querySelector(selector.carousel.wrapper) || null;
  element.carousel.inner =
    document.querySelector(selector.carousel.inner) || null;
  element.carousel.button.zoom =
    document.querySelector(selector.carousel.button.zoom) || null;
  element.carousel.button.closeFullscreen =
    document.querySelectorAll(selector.carousel.button.closeFullscreen) || null;
  element.bottomBarInfo =
    document.querySelector(selector.bottomBarInfo) || null;
};

/**
 * Calculates and sets the height for the carousel
 * @returns {void}
 */
const calculateAndSetHeightForCarousel = () => {
  if (element.carousel.wrapper) {
    const documentComputedStyle = getComputedStyle(document.documentElement);
    const height = {
      viewport: window.innerHeight,
      header:
        documentComputedStyle.getPropertyValue('--header__base-height') || 0,
      hellobar:
        documentComputedStyle.getPropertyValue('--hellobar-height') || 0,
      bottomBarInfo: element.bottomBarInfo
        ? element.bottomBarInfo.clientHeight
        : 0
    };

    const carouselHeight =
      parseInt(height.viewport) -
      parseInt(height.header) -
      parseInt(height.hellobar) -
      parseInt(height.bottomBarInfo);

    element.carousel.wrapper.style.height = `${carouselHeight}px`;
  }
};

const openFullScreen = () => {
  if (
    !mediaQueryTablet.matches &&
    !hasClassElement(element.carousel.wrapper, classes.is.fullscreen)
  ) {
    const children = element.carousel.inner
      ? element.carousel.inner.children
      : [];

    // We fix the body so it doesn't scroll in mobile
    addClassToElement(element.carousel.wrapper, classes.is.fullscreen);
    setHtmlUnscrollable();
    swiper.zoom.enable();

    for (var i = 0; i < children.length; i++) {
      const currentChild = element.carousel.inner.children[i];
      const currentImage = currentChild.querySelector(selector.imageFullscreen);

      if (currentImage) {
        currentImage.src = currentChild.dataset.fullscreenImage;
      }
    }
  }
};

const closeFullScreen = (e) => {
  e.stopPropagation();
  if (!mediaQueryTablet.matches) {
    // We reset both position and scrollTop so it goes back where it was
    setHtmlScrollable();
    removeClassToElement(element.carousel.wrapper, classes.is.fullscreen);
    swiper.zoom.out();
    swiper.zoom.disable();
  }
};

const initCarousel = () => {
  initElements();
  // init Swiper
  swiper = new Swiper(selector.swiper.wrapper, {
    modules: [Pagination, Zoom],
    zoom: true,
    loop: true,
    pagination: {
      el: selector.swiper.pagination
    }
  });

  // disable zoom
  swiper.zoom.disable();

  // open full screen when clicking on the image
  if (element.carousel.inner) {
    element.carousel.inner.addEventListener('click', openFullScreen);
  }

  // open full screen when clicking on the zoom button
  if (element.carousel.button.zoom) {
    element.carousel.button.zoom.addEventListener('click', openFullScreen);
  }

  // close full screen when clicking on the close button
  if (element.carousel.button.closeFullscreen.length > 0) {
    element.carousel.button.closeFullscreen.forEach((button) => {
      button.addEventListener('click', closeFullScreen);
    });
  }

  // if the page loads in desktop resolution, we disable the swiper
  if (mediaQueryTablet.matches) {
    swiper.disable();
  }

  // if the resolution changes, we enable or disable the swiper
  mediaQueryTablet.addEventListener('change', function (mm) {
    if (!mm.matches) {
      swiper.enable();
      removeClassToElement(element.carousel.inner, className.transformNone);
    } else {
      swiper.destroy(false, true);
      swiper.disable();
      removeClassToElement(element.carousel.wrapper, classes.is.fullscreen);
      htmlElementScroll = 0;
      addClassToElement(element.carousel.inner, className.transformNone);
    }
  });

  // calculate and set the height for the carousel
  calculateAndSetHeightForCarousel();
};

export { initCarousel };
